import React, { useMemo, useState } from "react";
import { Plus } from "lucide-react";

import TaskItem from "../../components/TaskItem";
import PhaseTasksGroup from "./PhaseTasksGroup";

import taskService from "../../services/taskService";

/* ===================================================== */
/* TASKS TAB                                             */
/* ===================================================== */

export default function TasksTab({
  projectId,
  tasks = [],
  setTasks,
  phases = [],
  users = [],
  isPro = false,
}) {
  const [newTitle, setNewTitle] = useState("");
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState(null);

  /* ===================================================== */
  /* 🧠 NORMALIZE ALL TASKS ONCE (CRITICAL FIX)            */
  /* ===================================================== */

  const normalizedTasks = useMemo(() => {
    return tasks
      .map((task) => {
        if (!task || typeof task !== "object") return null;

        const t = task._id ? task : task.task;
        if (!t || !t._id) return null;

        return {
          ...t,
          _id: String(t._id),
          phaseId:
            typeof t.phaseId === "string"
              ? t.phaseId
              : t.phaseId?._id
              ? String(t.phaseId._id)
              : null,
        };
      })
      .filter(Boolean);
  }, [tasks]);

  /* ===================================================== */
  /* CREATE TASK                                           */
  /* ===================================================== */

  const createTaskHandler = async () => {
      console.log("TITLE:", newTitle); // 👈 ADD THIS
    const title = newTitle.trim();

    if (!title) {
      setError("Task title is required");
      return;
    }

    try {
      setCreating(true);
      setError(null);

      const created = await taskService.createTask(
        projectId,
        
        { title }
      );

     if (created) {
      const fresh = await taskService.getTasks(projectId);
      setTasks(fresh);
    }

      setNewTitle("");
    } catch (err) {
      setError(err.message || "Failed to create task");
    } finally {
      setCreating(false);
    }
  };

  /* ===================================================== */
  /* TOGGLE STATUS                                         */
  /* ===================================================== */

  const handleToggle = async (task) => {
    const previous = task.status;
    const next = previous === "completed" ? "pending" : "completed";

    setTasks((prev) =>
      prev.map((t) =>
        t._id === task._id ? { ...t, status: next } : t
      )
    );

    try {
      await taskService.setTaskStatus(task._id, next);
    } catch (err) {
      setTasks((prev) =>
        prev.map((t) =>
          t._id === task._id ? { ...t, status: previous } : t
        )
      );

      alert(err.message || "Failed to update task");
    }
  };

  /* ===================================================== */
  /* DELETE TASK                                           */
  /* ===================================================== */

  const handleDelete = async (taskId) => {
    if (!window.confirm("Delete this task?")) return;

    const snapshot = [...tasks];

    setTasks((prev) => prev.filter((t) => t._id !== taskId));

    try {
      await taskService.deleteTask(taskId);
    } catch (err) {
      setTasks(snapshot);
      alert(err.message || "Failed to delete task");
    }
  };

  /* ===================================================== */
  /* GROUP TASKS                                           */
  /* ===================================================== */

  const { tasksByPhase } = useMemo(() => {
    const byPhase = {};
    const unassigned = [];

    normalizedTasks.forEach((t) => {
      if (!t.phaseId) {
        unassigned.push(t);
      } else {
        if (!byPhase[t.phaseId]) byPhase[t.phaseId] = [];
        byPhase[t.phaseId].push(t);
      }
    });

    return { unassignedTasks: unassigned, tasksByPhase: byPhase };
  }, [normalizedTasks]);

  /* ===================================================== */
  /* 📊 TRUE TASK COUNT (FIXES EMPTY STATE BUG)            */
  /* ===================================================== */

  const totalTasksCount = useMemo(() => {
    return (
      normalizedTasks.length
    );
  }, [normalizedTasks]);

  /* ===================================================== */
  /* RENDER                                                */
  /* ===================================================== */

  return (
    <div className="space-y-8">

      {/* QUICK ADD */}
      <div className="rounded-xl border bg-white p-4">
          {/* Encryption Notice */}
        <div className="mb-4 rounded-lg border border-green-200 bg-green-50 px-3 py-2.5">
          <p className="text-sm text-green-800">
            🔒 Information entered on this platform is end-to-end encrypted.
          </p>
        </div>
        <div className="flex items-center gap-2">

          <input
            value={newTitle}
            onChange={(e) => {
              setNewTitle(e.target.value);
              if (error) setError(null);
            }}
            onKeyDown={(e) =>
              e.key === "Enter" && createTaskHandler()
            }
            placeholder="Add a task..."
            className="flex-1 rounded-lg border px-3 py-2 text-sm"
          />

          <button
            onClick={createTaskHandler}
            disabled={creating}
            className="flex items-center gap-1 rounded-lg bg-black px-3 py-2 text-sm text-white disabled:opacity-50"
          >
            <Plus className="w-4 h-4" />
            {creating ? "Adding..." : "Add"}
          </button>

        </div>

        {error && (
          <div className="mt-2 text-sm text-red-600">
            {error}
          </div>
        )}
      </div>

      {/* ===================================================== */}
      {/* FREE PLAN                                            */}
      {/* ===================================================== */}

      {!isPro && (
        <div className="rounded-xl border bg-white">
          <div className="px-4 py-3 border-b bg-gray-50">
            <h3 className="font-medium">Tasks</h3>
          </div>

          <ul className="divide-y">
            {normalizedTasks.map((task) => (
              <TaskItem
                key={task._id}
                task={task}
                users={users}
                phases={[]}
                onToggle={handleToggle}
                onDelete={() => handleDelete(task._id)}
                onLocalUpdate={(updated) =>
                  setTasks((prev) =>
                    prev.map((p) =>
                      p._id === updated._id ? updated : p
                    )
                  )
                }
              />
            ))}
          </ul>
        </div>
      )}

      {/* ===================================================== */}
      {/* PRO PLAN                                             */}
      {/* ===================================================== */}

      {isPro &&
        phases.map((phase) => (
          <PhaseTasksGroup
            key={phase._id}
            phase={phase}
            projectId={projectId}
            users={users}
            allPhases={phases}
            tasks={tasksByPhase[String(phase._id)] || []}
            onTasksUpdate={setTasks}
          />
        ))}

        {isPro && (
          <PhaseTasksGroup
            key="unassigned"
            phase={{ _id: "unassigned", name: "General" }}
            projectId={projectId}
            users={users}
            allPhases={phases}
            tasks={normalizedTasks.filter((t) => !t.phaseId)}
            onTasksUpdate={setTasks}
          />
        )}

      {/* ✅ FIXED EMPTY STATE */}
      {totalTasksCount === 0 && (
        <div className="rounded-xl border bg-white p-6 text-center text-gray-500">
          No tasks yet. Add your first task above.
        </div>
      )}

    </div>
  );
}